import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { format, subDays, subHours, subMinutes, subSeconds, addSeconds, addMinutes, differenceInMilliseconds, differenceInDays, isAfter, isBefore, startOfDay } from 'date-fns';
import { Calendar as CalendarIcon, Clock, AlertCircle } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { ChevronLeft, ChevronRight } from 'lucide-react';

type InputMode = 'relative' | 'around' | 'custom';

// Relative time presets
const RELATIVE_PRESETS = [
  { label: 'Last 15 seconds', value: '15s', getRange: () => ({ start: subSeconds(new Date(), 15), end: new Date() }) },
  { label: 'Last 30 seconds', value: '30s', getRange: () => ({ start: subSeconds(new Date(), 30), end: new Date() }) },
  { label: 'Last 1 minute', value: '1m', getRange: () => ({ start: subMinutes(new Date(), 1), end: new Date() }) },
  { label: 'Last 5 minutes', value: '5m', getRange: () => ({ start: subMinutes(new Date(), 5), end: new Date() }) },
  { label: 'Last 10 minutes', value: '10m', getRange: () => ({ start: subMinutes(new Date(), 10), end: new Date() }) },
  { label: 'Last 15 minutes', value: '15m', getRange: () => ({ start: subMinutes(new Date(), 15), end: new Date() }) },
  { label: 'Last 30 minutes', value: '30m', getRange: () => ({ start: subMinutes(new Date(), 30), end: new Date() }) },
  { label: 'Last 1 hour', value: '1h', getRange: () => ({ start: subHours(new Date(), 1), end: new Date() }) },
] as const;

// Duration units for "around a time"
const DURATION_UNITS = [
  { label: 'Seconds', value: 'seconds' },
  { label: 'Minutes', value: 'minutes' },
] as const;

type DurationUnit = typeof DURATION_UNITS[number]['value'];

export interface TimeRange {
  start: Date;
  end: Date;
}

export interface TimeRangePickerProps {
  value?: TimeRange;
  onChange?: (range: TimeRange | undefined) => void;
  maxDurationHours?: number;
  maxAgeDays?: number;
  className?: string;
  disabled?: boolean;
}

interface TimeInputProps {
  value: Date | undefined;
  onChange: (date: Date) => void;
  label: string;
  minDate?: Date;
  maxDate?: Date;
}

function TimeInput({ value, onChange, label, minDate, maxDate }: TimeInputProps) {
  const [month, setMonth] = React.useState<Date>(value ?? new Date());
  const [isOpen, setIsOpen] = React.useState(false);

  const hours = React.useMemo(() => Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')), []);
  const minutes = React.useMemo(() => Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0')), []);
  const seconds = React.useMemo(() => Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0')), []);

  const selectedHour = value ? String(value.getHours()).padStart(2, '0') : '00';
  const selectedMinute = value ? String(value.getMinutes()).padStart(2, '0') : '00';
  const selectedSecond = value ? String(value.getSeconds()).padStart(2, '0') : '00';

  const updateTime = (type: 'hour' | 'minute' | 'second', val: string) => {
    const newDate = value ? new Date(value) : new Date();
    switch (type) {
      case 'hour':
        newDate.setHours(parseInt(val, 10));
        break;
      case 'minute':
        newDate.setMinutes(parseInt(val, 10));
        break;
      case 'second':
        newDate.setSeconds(parseInt(val, 10));
        break;
    }
    onChange(newDate);
  };

  const handleDaySelect = (day: Date | undefined) => {
    if (!day) return;
    const newDate = value ? new Date(value) : new Date();
    newDate.setFullYear(day.getFullYear(), day.getMonth(), day.getDate());
    onChange(newDate);
    setMonth(day);
  };

  const isDateDisabled = (date: Date) => {
    if (minDate && isBefore(date, startOfDay(minDate))) return true;
    if (maxDate && isAfter(date, maxDate)) return true;
    return false;
  };

  return (
    <div className="space-y-2">
      <Label className="text-xs text-gray-400">{label}</Label>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              'w-full justify-start bg-[#111] text-left font-normal',
              !value && 'text-muted-foreground'
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value ? format(value, 'MMM d, yyyy HH:mm:ss') : 'Select date & time'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <DayPicker
            mode="single"
            selected={value}
            month={month}
            onMonthChange={setMonth}
            onSelect={handleDaySelect}
            disabled={isDateDisabled}
            className="p-3"
            classNames={{
              months: 'flex flex-col sm:flex-row space-y-4 sm:space-y-0 justify-center',
              month: 'flex flex-col items-center space-y-4',
              month_caption: 'flex justify-center pt-1 relative items-center',
              caption_label: 'text-sm font-medium text-white',
              nav: 'space-x-1 flex items-center',
              button_previous: 'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute left-5 top-5',
              button_next: 'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute right-5 top-5',
              month_grid: 'w-full border-collapse space-y-1',
              weekdays: 'flex',
              weekday: 'text-gray-500 rounded-md w-9 font-normal text-[0.8rem]',
              week: 'flex w-full mt-2',
              day: 'h-9 w-9 text-center text-sm p-0 relative rounded-md',
              day_button: 'h-9 w-9 p-0 font-normal rounded-md hover:bg-[#222] text-white',
              selected: '[&>button]:!bg-lime [&>button]:!text-black',
              today: 'ring-1 ring-lime/50',
              outside: 'text-gray-600 opacity-50',
              disabled: 'text-gray-600 opacity-50 cursor-not-allowed',
            }}
            components={{
              Chevron: (props) =>
                props.orientation === 'left' ? (
                  <ChevronLeft className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                ),
            }}
          />
          <div className="border-t border-[#2E3835] p-3">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <Select value={selectedHour} onValueChange={(v) => updateTime('hour', v)}>
                <SelectTrigger className="w-[70px]">
                  <SelectValue placeholder="HH" />
                </SelectTrigger>
                <SelectContent>
                  {hours.map((h) => (
                    <SelectItem key={h} value={h}>{h}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span>:</span>
              <Select value={selectedMinute} onValueChange={(v) => updateTime('minute', v)}>
                <SelectTrigger className="w-[70px]">
                  <SelectValue placeholder="MM" />
                </SelectTrigger>
                <SelectContent>
                  {minutes.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span>:</span>
              <Select value={selectedSecond} onValueChange={(v) => updateTime('second', v)}>
                <SelectTrigger className="w-[70px]">
                  <SelectValue placeholder="SS" />
                </SelectTrigger>
                <SelectContent>
                  {seconds.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export function TimeRangePicker({
  value,
  onChange,
  maxDurationHours = 1,
  maxAgeDays = 7,
  className,
  disabled = false,
}: TimeRangePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [inputMode, setInputMode] = React.useState<InputMode>('relative');
  const [selectedPreset, setSelectedPreset] = React.useState<string | null>(null);
  const [startDate, setStartDate] = React.useState<Date | undefined>(value?.start);
  const [endDate, setEndDate] = React.useState<Date | undefined>(value?.end);

  // Around a time state
  const [aroundDate, setAroundDate] = React.useState<Date | undefined>(undefined);
  const [aroundDuration, setAroundDuration] = React.useState<number>(30);
  const [aroundUnit, setAroundUnit] = React.useState<DurationUnit>('seconds');

  const now = new Date();
  const minDate = subDays(now, maxAgeDays);

  // Calculate range from "around a time" inputs
  const aroundRange = React.useMemo(() => {
    if (!aroundDate) return null;

    const durationInSeconds = aroundUnit === 'minutes' ? aroundDuration * 60 : aroundDuration;
    const start = subSeconds(aroundDate, durationInSeconds);
    const end = addSeconds(aroundDate, durationInSeconds);

    return { start, end };
  }, [aroundDate, aroundDuration, aroundUnit]);

  // Get active start/end based on mode
  const activeStart = inputMode === 'around' ? aroundRange?.start : startDate;
  const activeEnd = inputMode === 'around' ? aroundRange?.end : endDate;

  // Validation
  const validationError = React.useMemo(() => {
    if (!activeStart || !activeEnd) return null;

    if (isAfter(activeStart, activeEnd)) {
      return 'Start time must be before end time';
    }

    // Use milliseconds for precise duration check (not truncated hours)
    const durationMs = differenceInMilliseconds(activeEnd, activeStart);
    const maxDurationMs = maxDurationHours * 60 * 60 * 1000;
    if (durationMs > maxDurationMs) {
      // For "around a time" mode, explain that ± means the duration is doubled
      if (inputMode === 'around') {
        const totalMinutes = Math.round(durationMs / 60000);
        return `± ${aroundDuration} ${aroundUnit} = ${totalMinutes} minutes total, which exceeds ${maxDurationHours} hour${maxDurationHours > 1 ? 's' : ''}`;
      }
      return `Duration cannot exceed ${maxDurationHours} hour${maxDurationHours > 1 ? 's' : ''}`;
    }

    const ageDays = differenceInDays(now, activeStart);
    if (ageDays > maxAgeDays) {
      return `Start time cannot be more than ${maxAgeDays} days ago`;
    }

    if (isAfter(activeEnd, now)) {
      return 'End time cannot be in the future';
    }

    return null;
  }, [activeStart, activeEnd, maxDurationHours, maxAgeDays, now, inputMode, aroundDuration, aroundUnit]);

  const isValid = activeStart && activeEnd && !validationError;

  const handlePresetClick = (preset: typeof RELATIVE_PRESETS[number]) => {
    const range = preset.getRange();
    setStartDate(range.start);
    setEndDate(range.end);
    setSelectedPreset(preset.value);
    setInputMode('relative');
    // Auto-apply relative presets
    onChange?.(range);
    setIsOpen(false);
  };

  const handleApply = () => {
    if (isValid && activeStart && activeEnd) {
      onChange?.({ start: activeStart, end: activeEnd });
      setIsOpen(false);
    }
  };

  const handleCancel = () => {
    setStartDate(value?.start);
    setEndDate(value?.end);
    setIsOpen(false);
  };

  // Update internal state when value changes externally
  React.useEffect(() => {
    setStartDate(value?.start);
    setEndDate(value?.end);
  }, [value]);

  // Clear preset when manually editing dates
  const handleStartChange = (date: Date) => {
    setStartDate(date);
    setSelectedPreset(null);
    setInputMode('custom');
  };

  const handleEndChange = (date: Date) => {
    setEndDate(date);
    setSelectedPreset(null);
    setInputMode('custom');
  };

  const handleAroundDateChange = (date: Date) => {
    setAroundDate(date);
    setSelectedPreset(null);
    setInputMode('around');
  };

  const displayText = value
    ? `${format(value.start, 'MMM d, HH:mm')} - ${format(value.end, 'MMM d, HH:mm')}`
    : 'Select time range';

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <Button
          variant="outline"
          className={cn(
            'w-full justify-start bg-[#111] text-left font-normal',
            !value && 'text-muted-foreground',
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {displayText}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" side="bottom">
        <div className="flex">
          {/* Left side: Mode selection and presets */}
          <div className="border-r border-[#2E3835] w-[200px]">
            {/* Relative time presets */}
            <div className="p-2 border-b border-[#2E3835]">
              <button
                onClick={() => setInputMode('relative')}
                className={cn(
                  'w-full text-left px-2 py-1.5 text-xs font-medium rounded text-lime',
                  inputMode === 'relative' ? 'bg-[#222]' : 'hover:bg-[#1a1a1a]'
                )}
              >
                Relative time
              </button>
              {inputMode === 'relative' && (
                <div className="mt-1">
                  {RELATIVE_PRESETS.map((preset) => (
                    <button
                      key={preset.value}
                      onClick={() => handlePresetClick(preset)}
                      className={cn(
                        'w-full text-left px-2 py-1.5 text-sm rounded hover:bg-[#222] flex justify-between items-center text-white',
                        selectedPreset === preset.value && 'bg-[#222]'
                      )}
                    >
                      <span>{preset.label}</span>
                      <span className="text-xs text-gray-500">{preset.value}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Custom range option */}
            <div className="p-2 border-b border-[#2E3835]">
              <button
                onClick={() => setInputMode('custom')}
                className={cn(
                  'w-full text-left px-2 py-1.5 text-xs font-medium rounded text-lime',
                  inputMode === 'custom' ? 'bg-[#222]' : 'hover:bg-[#1a1a1a]'
                )}
              >
                Start and end times
              </button>
            </div>

            {/* Around a time option */}
            <div className="p-2">
              <button
                onClick={() => setInputMode('around')}
                className={cn(
                  'w-full text-left px-2 py-1.5 text-xs font-medium rounded text-lime',
                  inputMode === 'around' ? 'bg-[#222]' : 'hover:bg-[#1a1a1a]'
                )}
              >
                Around a time
              </button>
            </div>
          </div>

          {/* Right side: Input fields based on mode */}
          <div className="p-4 w-[300px]">
            {inputMode === 'relative' && (
              <div className="text-sm text-gray-400">
                Select a relative time preset from the left panel.
              </div>
            )}

            {inputMode === 'custom' && (
              <>
                <div className="text-xs font-medium text-gray-400 mb-3">
                  Custom range (max {maxDurationHours}h, last {maxAgeDays} days)
                </div>
                <div className="space-y-4">
                  <TimeInput
                    label="Start"
                    value={startDate}
                    onChange={handleStartChange}
                    minDate={minDate}
                    maxDate={now}
                  />
                  <TimeInput
                    label="End"
                    value={endDate}
                    onChange={handleEndChange}
                    minDate={startDate || minDate}
                    maxDate={now}
                  />
                </div>
              </>
            )}

            {inputMode === 'around' && (
              <>
                <div className="text-xs font-medium text-gray-400 mb-3">
                  Around a time (± duration)
                </div>
                <div className="space-y-4">
                  <TimeInput
                    label="Center time"
                    value={aroundDate}
                    onChange={handleAroundDateChange}
                    minDate={minDate}
                    maxDate={now}
                  />

                  <div className="space-y-2">
                    <Label className="text-xs text-gray-400">Duration (±)</Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        min={1}
                        max={aroundUnit === 'minutes' ? 30 : 1800}
                        value={aroundDuration}
                        onChange={(e) => setAroundDuration(Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-20"
                      />
                      <Select value={aroundUnit} onValueChange={(v) => setAroundUnit(v as DurationUnit)}>
                        <SelectTrigger className="flex-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DURATION_UNITS.map((unit) => (
                            <SelectItem key={unit.value} value={unit.value}>
                              {unit.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {aroundRange && (
                    <div className="text-xs text-gray-400 bg-[#1a1a1a] p-2 rounded border border-[#2E3835]">
                      <div>From: {format(aroundRange.start, 'MMM d, HH:mm:ss')}</div>
                      <div>To: {format(aroundRange.end, 'MMM d, HH:mm:ss')}</div>
                    </div>
                  )}
                </div>
              </>
            )}

            {validationError && (
              <div className="mt-3 flex items-center gap-2 text-sm text-red-500">
                <AlertCircle className="h-4 w-4" />
                {validationError}
              </div>
            )}

            {inputMode !== 'relative' && (
              <div className="mt-4 flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleApply} disabled={!isValid}>
                  Apply
                </Button>
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export { RELATIVE_PRESETS };
